import { createSelector } from 'reselect';
import {
  path,
  prop,
  equals,
  pipe,
  find,
  propEq,
  propOr,
  concat,
  compose,
  map,
  mergeLeft,
  reject,
} from 'ramda';

import { FORM_NAMES } from 'constants/forms';
import { getFormattedTelecom, formatDate, formatDateOr } from 'utilities/formatters';
import i18n from 'services/i18n';
import {
  TECHNICIAN,
  DOCTOR,
  RESEARCHER,
  PATIENT,
  SUPER_TECHNICIAN,
  SUPER_RESEARCHER,
} from 'constants/userRoles';

import {
  getPatientQuestionnaireResponseDeviceClinicSpecial,
  getPatientQuestionnaireResponseSatelliteDeviceClinic,
  getPatientQuestionnaireResponseLocalDeviceClinicPhone,
} from 'store/selectors/patientQuestionnaireResponse';

import { NA } from 'constants/common';

const localState = prop('user');

export const getUserRole = createSelector(
  localState,
  prop('role'),
);

export const getUserRegion = createSelector(
  localState,
  prop('region'),
);

export const getAllPractionersRequstArgs = createSelector(
  getUserRegion,
  region => ({
    region,
  }),
);

export const isTechnician = createSelector(
  getUserRole,
  equals(TECHNICIAN),
);

export const isDoctor = createSelector(
  getUserRole,
  equals(DOCTOR),
);

export const isResearcher = createSelector(
  getUserRole,
  equals(RESEARCHER),
);

export const isPatient = createSelector(
  getUserRole,
  equals(PATIENT),
);

export const isSuperResearcher = createSelector(
  getUserRole,
  equals(SUPER_RESEARCHER),
);

export const isSuperTechnician = createSelector(
  getUserRole,
  equals(SUPER_TECHNICIAN),
);

export const getUserData = createSelector(
  localState,
  prop('data'),
);

export const getUserResourceType = createSelector(
  getUserData,
  prop('resourceType'),
);

export const getUserPreferedLanguageCode = createSelector(
  getUserData,
  path(['communication', '0', 'language', 'coding', '0', 'code']),
);

export const getUserIsSignedAgreement = createSelector(
  getUserData,
  pipe(
    propOr([], 'extension'),
    find(propEq('url', 'isSignedAgreement.v1')),
    propOr(false, 'valueBoolean'),
  ),
);

export const getUserProfileEmailNotifications = createSelector(
  getUserData,
  pipe(
    propOr([], 'extension'),
    find(propEq('url', 'contact.emailNotifications')),
    propOr(false, 'valueBoolean'),
  ),
);

export const getUserProfileCreatedOn = createSelector(
  getUserData,
  pipe(
    propOr([], 'extension'),
    find(propEq('url', 'created-on')),
    prop('valueDateTime'),
    date => (date ? formatDate(i18n.t('dateTimeFormat.profileCreatedOn'))(date) : NA),
  ),
);

export const getUserPreferedLanguageText = createSelector(
  getUserData,
  path(['communication', '0', 'language', 'text']),
);

export const getUserName = createSelector(
  getUserData,
  path(['name', '0', 'text']),
);

export const getUserId = createSelector(
  getUserData,
  prop('id'),
);

export const getUserRequestReportsParams = createSelector(
  getUserId,
  patientId => ({ patientId, status: 'final,preliminary' }),
);

export const getUserDob = createSelector(
  getUserData,
  prop('birthDate'),
);

export const getUserGender = createSelector(
  getUserData,
  pipe(
    prop('gender'),
    gender => gender || NA,
  ),
);

export const getUserFormattedDob = createSelector(
  getUserDob,
  date => formatDateOr(NA, i18n.t('dateTimeFormat.dob'))(date),
);

export const getUserContact = createSelector(
  getUserData,
  prop('contact'),
);

export const getUserTelecom = createSelector(
  getUserData,
  propOr([], 'telecom'),
);

export const getUserContactMethod = createSelector(
  getUserTelecom,
  path(['0', 'system']),
);

export const getUserProfileEmail = createSelector(
  getUserTelecom,
  pipe(
    find(propEq('system', 'email')),
    prop('value'),
  ),
);

export const getPathParamWithPatientId = createSelector(
  getUserId,
  patientId => ({ patientId }),
);

export const getUserDeviceName = createSelector(
  getUserData,
  pipe(
    prop('extension'),
    find(propEq('url', 'device.name')),
    propOr('', 'valueCode'),
  ),
);

export const getLocalClinicInfo = createSelector(
  getPatientQuestionnaireResponseDeviceClinicSpecial,
  getPatientQuestionnaireResponseSatelliteDeviceClinic,
  getPatientQuestionnaireResponseLocalDeviceClinicPhone,
  (clinicNameSpecial, clinicName, clinicPhone) => [
    { title: 'patient.clinicName1', value: clinicNameSpecial },
    { title: 'patient.clinicName2', value: clinicName },
    { title: 'patient.localClinicPhone', value: clinicPhone },
  ],
);

export const getSidebarPatientsInfoList = createSelector(
  getUserData,
  getUserProfileCreatedOn,
  getLocalClinicInfo,
  (userData, createdDate, clinicData) =>
    compose(
      concat([
        {
          title: 'patient.personalContactDetails',
          items: getFormattedTelecom(prop('telecom')(userData)),
        },
      ]),
      concat(
        map(
          ({ name, relationship, telecom: contactTelecom }) => ({
            title: i18n.t(path(['0', 'coding', '0', 'display'], relationship)),
            items: compose(
              concat([{ value: name.text }]),
              concat(getFormattedTelecom(contactTelecom)),
            )([]),
          }),
          prop('contact')(userData),
        ),
      ),
      concat([
        {
          title: 'patient.preferredLanguage',
          items: [{ value: i18n.t(path(['communication', '0', 'language', 'text'])(userData)) }],
        },
      ]),
      concat([
        {
          title: 'patient.profileCreatedOn',
          items: [
            {
              value: createdDate,
            },
          ],
        },
      ]),
      concat([
        {
          title: 'patient.localClinicInfo',
          items: clinicData,
        },
      ]),
    )([]),
);

export const getEditedPatientsNotificationsData = createSelector(
  path(['form', FORM_NAMES.notificationsSetting, 'values']),
  getUserData,
  ({ allowEmail } = {}, editedPatient) =>
    mergeLeft(
      {
        extension: pipe(
          propOr([], 'extension'),
          reject(propEq('url', 'contact.emailNotifications')),
          concat([
            {
              url: 'contact.emailNotifications',
              valueBoolean: allowEmail,
            },
          ]),
        )(editedPatient),
      },
      editedPatient,
    ),
);

export const getPatientAcceptAgreementData = createSelector(
  getUserData,
  patient =>
    mergeLeft(
      {
        extension: pipe(
          propOr([], 'extension'),
          reject(propEq('url', 'isSignedAgreement.v1')),
          concat([
            {
              url: 'isSignedAgreement.v1',
              valueBoolean: true,
            },
          ]),
        )(patient),
      },
      patient,
    ),
);
